/* The issue tracker (designs/issue-tracker.md): a bug or a feature request
   about Hermes itself, filed by anyone signed in from the web app or through
   Claude, triaged by an Admin.

   Hermes is the tracker of record (IT1). The one link to Jira or GitHub is a
   field on the triage stream, validated here and never fetched — Hermes says
   "tracked in ABC-123", never what Jira says about it (IT5). */

import type { EntryPath, Stamp } from "./organizer-roles";
import { ENTRY_PATH_LABEL } from "./organizer-roles";

export const ISSUE_KINDS = ["bug", "feature"] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];
export const ISSUE_KIND_LABEL: Record<IssueKind, string> = { bug: "Bug", feature: "Feature request" };

/* IT4: three open, three closed; no revision yet reads as new */
export const ISSUE_STATES = ["new", "accepted", "in-progress", "done", "declined", "duplicate"] as const;
export type IssueState = (typeof ISSUE_STATES)[number];
export const ISSUE_STATE_LABEL: Record<IssueState, string> = {
  new: "New",
  accepted: "Accepted",
  "in-progress": "In progress",
  done: "Done",
  declined: "Declined",
  duplicate: "Duplicate",
};
const OPEN: readonly IssueState[] = ["new", "accepted", "in-progress"];
export const isOpenState = (s: IssueState): boolean => OPEN.includes(s);

export const ISSUE_PRIORITIES = ["low", "medium", "high"] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];
export const ISSUE_PRIORITY_LABEL: Record<IssuePriority, string> = { low: "Low", medium: "Medium", high: "High" };
/* never defaulted (settled decision 2) */
export const NOT_SET = "Not set";
export const NOT_LINKED = "Not linked";

export const ISSUE_LIMITS = { title: 120, text: 4000, view: 200, resolution: 2000, userAgent: 300, url: 500 } as const;

/* what the requester gives (IT3), cleaned */
export interface IssueInput {
  kind: IssueKind;
  title: string;
  description: string;
  steps: string | null;
  expected: string | null;
  context: { view: string | null };
}

/* the filed record — written once, never edited (IT3) */
export interface Issue extends IssueInput {
  id: string;
  userAgent: string | null;
  filed: Stamp;
}

export interface TriageValues {
  state: IssueState;
  priority: IssuePriority | null;
  trackerUrl: string | null;
  resolution: string | null;
  duplicateOfId: string | null;
}

/* one Admin revision on the triage stream (IT4). `seq` numbers an issue's
   revisions 1, 2, 3… and is unique per issue, so two saves made from the
   same revision cannot both land (IT4a). */
export interface Triage extends TriageValues {
  seq: number;
  recorded: Stamp;
}

export type TriagePatch = Partial<TriageValues>;

/* IT4a: a change says which revision it was made from — 0 for a report
   nobody has triaged — so a save from a stale screen is refused, not
   allowed to put back values another Admin just changed */
export const basedOnReason = (v: unknown): string | null =>
  Number.isInteger(v) && (v as number) >= 0
    ? null
    : "send basedOn: the revision you edited from (the report's `revision`; 0 if it was never triaged)";

export const STALE_TRIAGE =
  "this report changed since you opened it — reload it to see the change, then make yours again";

export interface TrackerLink {
  type: "jira" | "github";
  label: string;
  url: string;
}

export interface IssueView extends Issue {
  /* how many triage revisions it has — what a change sends back as basedOn */
  revision: number;
  state: IssueState;
  priority: IssuePriority | null;
  resolution: string | null;
  duplicateOfId: string | null;
  tracker: TrackerLink | null;
  triaged: Stamp | null;
}

/* GET /api/issues — the scope travels with the list so an empty one cannot
   read as "nobody has filed anything" (IT6, CS5) */
export interface IssueList {
  scope: "mine" | "all";
  issues: IssueView[];
}

export interface IssueDetail extends IssueView {
  history: Triage[];
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const blank = (v: unknown): boolean => typeof v !== "string" || v.trim() === "";
const optionalText = (v: unknown): boolean => v === undefined || v === null || typeof v === "string";
const tooLong = (v: unknown, max: number): boolean => typeof v === "string" && v.trim().length > max;

/* IT3: a reason when the body cannot be filed, else null */
export const issueInputReason = (body: unknown): string | null => {
  if (!isObject(body)) return "an issue must be an object";
  if (!(ISSUE_KINDS as readonly unknown[]).includes(body.kind))
    return 'kind must be "bug" or "feature"';
  if (blank(body.title)) return "a title is required";
  if (tooLong(body.title, ISSUE_LIMITS.title)) return `the title can be at most ${ISSUE_LIMITS.title} characters`;
  if (blank(body.description)) return "a description is required — what happened, or what you would like";
  if (tooLong(body.description, ISSUE_LIMITS.text))
    return `the description can be at most ${ISSUE_LIMITS.text} characters`;
  for (const f of ["steps", "expected"] as const) {
    if (!optionalText(body[f])) return `${f} must be text`;
    if (tooLong(body[f], ISSUE_LIMITS.text)) return `${f} can be at most ${ISSUE_LIMITS.text} characters`;
    if (body.kind === "feature" && !blank(body[f]))
      return `${f} describes a bug — leave it out of a feature request`;
  }
  if (body.context !== undefined && body.context !== null) {
    if (!isObject(body.context)) return "context must be an object, like { view }";
    if (!optionalText(body.context.view)) return "context.view must be text";
    if (tooLong(body.context.view, ISSUE_LIMITS.view))
      return `context.view can be at most ${ISSUE_LIMITS.view} characters`;
  }
  return null;
};

const textOrNull = (v: unknown): string | null => (blank(v) ? null : (v as string).trim());

/* only the IT3 fields — a body claiming a filer, a path, or anything else is
   dropped; Hermes stamps those itself */
export const cleanIssueInput = (body: Record<string, unknown>): IssueInput => {
  const ctx = isObject(body.context) ? body.context : {};
  return {
    kind: body.kind as IssueKind,
    title: (body.title as string).trim(),
    description: (body.description as string).trim(),
    steps: textOrNull(body.steps),
    expected: textOrNull(body.expected),
    context: { view: textOrNull(ctx.view) },
  };
};

const JIRA_HOST = /^[a-z0-9][a-z0-9-]*\.atlassian\.net$/;
const JIRA_PATH = /^\/browse\/([A-Za-z][A-Za-z0-9_]*-[1-9][0-9]*)\/?$/;
const GITHUB_PATH = /^\/([A-Za-z0-9-]+)\/([A-Za-z0-9._-]+)\/(issues|pull)\/([1-9][0-9]*)\/?$/;

/* IT5: a Jira issue link or a GitHub issue / pull request link, normalised —
   https only, no credentials or port, query and fragment dropped. Anything
   else is null. An allowlist because the link is rendered as an anchor to
   every Admin, and a free URL field invites javascript: and phishing. */
export const trackerLinkFrom = (raw: string): TrackerLink | null => {
  const s = raw.trim();
  if (s.length === 0 || s.length > ISSUE_LIMITS.url) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || u.username || u.password || u.port) return null;
  if (JIRA_HOST.test(u.hostname)) {
    const m = JIRA_PATH.exec(u.pathname);
    if (!m) return null;
    const key = m[1]!.toUpperCase();
    return { type: "jira", label: key, url: `https://${u.hostname}/browse/${key}` };
  }
  if (u.hostname === "github.com") {
    const m = GITHUB_PATH.exec(u.pathname);
    if (!m) return null;
    const [, owner, repo, kind, n] = m;
    return { type: "github", label: `${owner}/${repo}#${n}`, url: `https://github.com/${owner}/${repo}/${kind}/${n}` };
  }
  return null;
};

export const TRACKER_SHAPES =
  "a Jira issue link (https://<site>.atlassian.net/browse/KEY-123) or a GitHub issue or pull request " +
  "link (https://github.com/<owner>/<repo>/issues/45)";

const PATCH_FIELDS = ["state", "priority", "trackerUrl", "resolution", "duplicateOfId"] as const;

/* IT4: merged triage, before stamping. undefined leaves the standing value,
   null clears it; the link is stored normalised; leaving Duplicate drops its
   target. Call only on a patch triagePatchReason accepted. */
export const mergeTriage = (current: TriageValues | null, patch: TriagePatch): TriageValues => {
  const pick = <K extends keyof TriageValues>(k: K, fallback: TriageValues[K]): TriageValues[K] =>
    patch[k] !== undefined ? (patch[k] as TriageValues[K]) : (current?.[k] ?? fallback);
  const state = pick("state", "new");
  const trackerUrl = pick("trackerUrl", null);
  return {
    state,
    priority: pick("priority", null),
    trackerUrl: trackerUrl === null ? null : (trackerLinkFrom(trackerUrl)?.url ?? null),
    resolution: textOrNull(pick("resolution", null)),
    duplicateOfId: state === "duplicate" ? pick("duplicateOfId", null) : null,
  };
};

/* IT4: a reason when an Admin's change cannot be saved, else null. Shape
   only — whether duplicateOfId names a real issue is the route's check,
   because this module has no store. */
export const triagePatchReason = (patch: unknown, current: TriageValues | null, selfId: string): string | null => {
  if (!isObject(patch)) return "a triage change must be an object";
  if (!PATCH_FIELDS.some((f) => patch[f] !== undefined))
    return `nothing to change — send any of ${PATCH_FIELDS.join(", ")}`;
  if (patch.state !== undefined && !(ISSUE_STATES as readonly unknown[]).includes(patch.state))
    return `state must be one of ${ISSUE_STATES.join(", ")}`;
  if (patch.priority !== undefined && patch.priority !== null && !(ISSUE_PRIORITIES as readonly unknown[]).includes(patch.priority))
    return `priority must be ${ISSUE_PRIORITIES.join(", ")}, or null for not set`;
  if (patch.trackerUrl !== undefined && patch.trackerUrl !== null) {
    if (typeof patch.trackerUrl !== "string") return `the tracker link must be ${TRACKER_SHAPES}`;
    if (!trackerLinkFrom(patch.trackerUrl)) return `the tracker link must be ${TRACKER_SHAPES}`;
  }
  if (!optionalText(patch.resolution)) return "resolution must be text";
  if (tooLong(patch.resolution, ISSUE_LIMITS.resolution))
    return `the resolution can be at most ${ISSUE_LIMITS.resolution} characters`;
  if (patch.duplicateOfId !== undefined && patch.duplicateOfId !== null && typeof patch.duplicateOfId !== "string")
    return "name the report this duplicates by its id";
  const merged = mergeTriage(current, patch as TriagePatch);
  if (patch.duplicateOfId != null && merged.state !== "duplicate")
    return "only a report marked Duplicate names the report it duplicates";
  if (merged.state === "duplicate") {
    const target = patch.duplicateOfId !== undefined ? patch.duplicateOfId : current?.duplicateOfId;
    if (blank(target)) return "name the report this duplicates";
    if ((target as string).trim() === selfId) return "a report cannot duplicate itself";
  }
  if ((merged.state === "declined" || merged.state === "duplicate") && merged.resolution === null)
    return `say why it is ${merged.state} — the requester sees this note`;
  return null;
};

export const issueViewFrom = (issue: Issue, latest: Triage | null): IssueView => ({
  ...issue,
  revision: latest?.seq ?? 0,
  state: latest?.state ?? "new",
  priority: latest?.priority ?? null,
  resolution: latest?.resolution ?? null,
  duplicateOfId: latest?.duplicateOfId ?? null,
  tracker: latest?.trackerUrl ? trackerLinkFrom(latest.trackerUrl) : null,
  triaged: latest?.recorded ?? null,
});

/* IT9: a cell a spreadsheet would run as a formula gets a leading ' — the
   requester writes these cells and an Admin opens the file in Excel */
export const csvGuard = (s: string): string => (/^[=+\-@\t\r]/.test(s) ? `'${s}` : s);
const csvCell = (v: string): string => {
  const s = csvGuard(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const viaLabel = (via: EntryPath | undefined): string => (via ? ENTRY_PATH_LABEL[via] : "");

const ISSUE_CSV_COLUMNS: ReadonlyArray<[string, (r: IssueView) => string]> = [
  ["Filed", (r) => r.filed.date.slice(0, 10)],
  ["Kind", (r) => ISSUE_KIND_LABEL[r.kind]],
  ["Title", (r) => r.title],
  ["State", (r) => ISSUE_STATE_LABEL[r.state]],
  ["Priority", (r) => (r.priority ? ISSUE_PRIORITY_LABEL[r.priority] : NOT_SET)],
  ["Tracker", (r) => r.tracker?.label ?? NOT_LINKED],
  ["Tracker link", (r) => r.tracker?.url ?? ""],
  ["Filed by", (r) => r.filed.by?.name ?? ""],
  ["Via", (r) => viaLabel(r.filed.via)],
  ["Page", (r) => r.context.view ?? ""],
  ["Description", (r) => r.description],
  ["Steps", (r) => r.steps ?? ""],
  ["Expected", (r) => r.expected ?? ""],
  ["Resolution", (r) => r.resolution ?? ""],
];

export const issueCsv = (rows: IssueView[]): string =>
  [
    ISSUE_CSV_COLUMNS.map(([h]) => csvCell(h)).join(","),
    ...rows.map((r) => ISSUE_CSV_COLUMNS.map(([, get]) => csvCell(get(r))).join(",")),
  ].join("\n") + "\n";

/* ── the Issues list's filters (IT9) — client-side over the one list, as the
   intake board filters its rows ── */
export interface IssueFilters {
  kind: "all" | IssueKind;
  /* open and closed are shortcuts over the six states */
  state: "all" | "open" | "closed" | IssueState;
  /* "none" = not set */
  priority: "all" | "none" | IssuePriority;
  /* "none" = not linked */
  tracker: "all" | "none" | TrackerLink["type"];
  /* a filer's userId, or "all" */
  filer: string;
  text: string;
}

export const EMPTY_ISSUE_FILTERS: IssueFilters = {
  kind: "all",
  state: "all",
  priority: "all",
  tracker: "all",
  filer: "all",
  text: "",
};

export const hasIssueFilters = (f: IssueFilters): boolean =>
  f.kind !== "all" ||
  f.state !== "all" ||
  f.priority !== "all" ||
  f.tracker !== "all" ||
  f.filer !== "all" ||
  f.text.trim() !== "";

const stateMatches = (want: IssueFilters["state"], s: IssueState): boolean =>
  want === "all" || (want === "open" ? isOpenState(s) : want === "closed" ? !isOpenState(s) : want === s);

export function filterIssues(rows: IssueView[], f: IssueFilters): IssueView[] {
  const text = f.text.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (f.kind === "all" || r.kind === f.kind) &&
      stateMatches(f.state, r.state) &&
      (f.priority === "all" || (f.priority === "none" ? r.priority === null : r.priority === f.priority)) &&
      (f.tracker === "all" || (f.tracker === "none" ? r.tracker === null : r.tracker?.type === f.tracker)) &&
      (f.filer === "all" || r.filed.by?.userId === f.filer) &&
      (text === "" || `${r.title}\n${r.description}`.toLowerCase().includes(text)),
  );
}
